import { Injectable, inject } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import {
  ConnectWebSocket,
  DisconnectWebSocket,
  WebSocketConnected,
  WebSocketDisconnected,
  WebSocketMessageReceived,
  SendWebSocketMessage,
  WebSocketError,
} from './websocket.actions';
import { WebSocketService } from '../services/websocket.service';

// Debug-only message log cap — prevents unbounded growth in long sessions.
const MAX_MESSAGE_LOG = 200;

export interface WebSocketStateModel {
  connected: boolean;
  messages: Array<{ type: string; payload: unknown }>;
  lastError: string | null;
}

@State<WebSocketStateModel>({
  name: 'websocket',
  defaults: {
    connected: false,
    messages: [],
    lastError: null,
  },
})
@Injectable()
export class WebSocketState {
  private wsService = inject(WebSocketService);

  @Selector()
  static connected(state: WebSocketStateModel): boolean {
    return state.connected;
  }

  @Selector()
  static messages(state: WebSocketStateModel): Array<{ type: string; payload: unknown }> {
    return state.messages;
  }

  @Selector()
  static lastError(state: WebSocketStateModel): string | null {
    return state.lastError;
  }

  @Action(ConnectWebSocket)
  connect(ctx: StateContext<WebSocketStateModel>) {
    this.wsService.connect(ctx);
  }

  @Action(DisconnectWebSocket)
  disconnect() {
    this.wsService.disconnect();
  }

  @Action(WebSocketConnected)
  onConnected(ctx: StateContext<WebSocketStateModel>) {
    ctx.patchState({ connected: true, lastError: null });
  }

  @Action(WebSocketDisconnected)
  onDisconnected(ctx: StateContext<WebSocketStateModel>) {
    ctx.patchState({ connected: false });
  }

  @Action(WebSocketMessageReceived)
  onMessage(ctx: StateContext<WebSocketStateModel>, action: WebSocketMessageReceived) {
    const state = ctx.getState();
    const messages = [...state.messages, action.payload];
    ctx.patchState({
      messages: messages.length > MAX_MESSAGE_LOG ? messages.slice(messages.length - MAX_MESSAGE_LOG) : messages,
    });
  }

  @Action(SendWebSocketMessage)
  sendMessage(_ctx: StateContext<WebSocketStateModel>, action: SendWebSocketMessage) {
    this.wsService.send(action.payload);
  }

  @Action(WebSocketError)
  onError(ctx: StateContext<WebSocketStateModel>, action: WebSocketError) {
    ctx.patchState({ lastError: action.message });
  }
}
